import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import TextHeader from "./TextHeader";
import { API_ROOT, webPath } from "../../apiconfig";

const sectionsLatest = [
  { title: "News", key: "newsData" },
  // { title: "Quick Bytes", key: "quickByteData" },
];

const sectionsLeadership = [
  { title: "Podcasts", key: "podcastData" },
  { title: "Featured Interview", key: "inteviewData" },
  { title: "Guest Posts", key: "guestPostData" },
];

const sectionsFeatures = [
  { title: "Articles", key: "articleData" },
  // { title: "Future Ready", key: "futureReadyData" },
  { title: "Learning Center", key: "learningData" },
];

const DropComp = React.memo(
  ({ className, section, handleSectionClick, maxItems }) => {
    const { cat_slug } = useParams();

    // ----- Break‑point detection -----
    const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
    const isLte1024 = useMediaQuery({ query: "(max-width: 1024px)" });
    const isLte768 = useMediaQuery({ query: "(max-width: 768px)" });

    // ----- Compute the maxWidth for the Leadership section -----
    const leadershipMaxWidth = useMemo(() => {
      if (isLte768) return "650px"; // ≤ 768 px
      // if (isLte1024) return "800px";
      // return "1000px";                  // > 1024 px (fallback / desktop)
    }, [isLte768, isLte1024]);

    const [headerData, setHeaderData] = useState({
      latest: {},
      leadership: {},
      features: {},
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeSection, setActiveSection] = useState(section);

    useEffect(() => {
      const fetchData = async () => {
        try {
          // Fetch data for all sections at once
          const [latestResponse, leadershipResponse, featuresResponse] =
            await Promise.all([
              fetch(`${API_ROOT}/api/post/latestPost`),
              fetch(`${API_ROOT}/api/post/leadership`),
              fetch(`${API_ROOT}/api/post/featured`),
            ]);

          const [latestData, leadershipData, featuresData] = await Promise.all([
            latestResponse.json(),
            leadershipResponse.json(),
            featuresResponse.json(),
          ]);

          setHeaderData({
            latest: latestData,
            leadership: leadershipData,
            features: featuresData,
          });
        } catch (error) {
          console.error("Error fetching data:", error);
          setError("Error fetching data");
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }, []);

    const renderSection = useCallback(
      (sectionData, sections, currentSectionName) => {
        return sections.map((section, sectionIndex) => {
          const sectionUrl = `/topic/${sectionData[section.key]?.[0]?.cat_slug || cat_slug}`;
          const columnClass =
            activeSection === "Latest"
              ? "col-12"
              : activeSection === "Features"
                ? "col-md-6"
                : "col-md-4";

          const sectionClass = isMobile
            ? "col-12"
            : `${columnClass} section-line`;
          const latestExtraCls =
            activeSection === "Latest" ? "latest-item" : "";
          // ---- NEW: add leadership‑item class when we are in the Leadership tab ----
          const leadershipExtraCls =
            activeSection === "Leadership" ? "leadership-item" : "";

          return (
         <div
            key={sectionIndex}
            className={`${sectionClass} ${latestExtraCls} ${leadershipExtraCls}`}
          >
              {isMobile ? (
                <>
                  <a
                    href={sectionUrl}
                    style={{ textDecoration: "none", color: "black" }}
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = sectionUrl;
                    }}
                  >
                    <p className="section-title pt-1">{section.title}</p>
                  </a>
                  <p className="see-more mt-2 mb-2" style={{ display: "none" }}>
                    <a
                      className="latest-anchor"
                      style={{ textDecoration: "none", color: "black" }}
                      href={sectionUrl}
                    >
                      See More
                    </a>
                  </p>
                </>
              ) : (
                <>
                  <p className="section-title mt-1">{section.title}</p>
                  {sectionData[section.key]?.slice(0, maxItems).map((x, i) => (
                    <React.Fragment key={i}>
                      <a
                        href={`/${x.cat_slug}/${x.post_name}`}
                        style={{ textDecoration: "none", color: "black" }}
                      >
                        <TextHeader
                          headerImg={`${webPath}${x.banner_img}?width=300`}
                          alt={x.post_name}
                          headerText={x.post_title}
                          headerDesc={x.post_author}
                          post_date={x.post_date}
                          headerDescClass="header-desc"
                        />
                      </a>
                      <p className="mt-2 section-line"></p>
                    </React.Fragment>
                  ))}
                  <p className="see-more mt-2 mb-2">
                    <a
                      className="latest-anchor"
                      style={{ textDecoration: "none", color: "black" }}
                      href={sectionUrl}
                    >
                      See More
                    </a>
                  </p>
                </>
              )}
            </div>
          );
        });
      },
      [maxItems, cat_slug, isMobile, activeSection],
    );

    const renderedSections = useMemo(() => {
      if (loading) {
        return null; // No skeleton loader, just return null for loading state
      }

      if (error) {
        return <p style={{ color: "red" }}>Error: {error}</p>; // Display error message
      }

      switch (section) {
        case "Latest":
          return renderSection(headerData.latest, sectionsLatest, "Latest");
        case "Leadership":
          return renderSection(
            headerData.leadership,
            sectionsLeadership,
            "Leadership",
          );
        case "Features":
          return renderSection(
            headerData.features,
            sectionsFeatures,
            "Features",
          );
        default:
          return null;
      }
    }, [headerData, section, renderSection, loading, error]);

    useEffect(() => {
      setActiveSection(section);
    }, [section]);

    return (
      // <div className={`container LatestData ${className}`}>
      <div
        className={`container LatestData ${className} ${
          activeSection === "Latest" ? "latest-container" : ""
        }`}
      >
        <div className="row">{renderedSections}</div>
      </div>
    );
  },
);

export default DropComp;
